import { call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import API from 'api';
import { Marker, MarkerStatus } from 'types/marker';
import i18n, { messages } from 'i18n';
import { getBestRides as loadBestRides } from 'common/getBestRides';

import { logout } from '../user/actions';
import { AppState } from '../interfaces';
import { getPlaces } from '../../common/getPlaces';
import { getLocationData } from '../../common/getLocationData';
import { getSearchStatus, sendLog } from '../app/acions';
import { eventsService } from '../../services/EventsService';

import { actionCreators } from './actions';

function* getMarkers() {
  const response = yield call(API.markers.list);

  switch (response.code) {
    case 200: {
      const { items } = response;
      yield put(actionCreators.getMarkers.success(items as Array<Marker>));
      break;
    }
    case 401: {
      toast.error(messages.sessionTimeout);
      yield put(logout());
      break;
    }
    case 404: {
      yield put(actionCreators.getMarkers.success([]));
      break;
    }
    case 422: {
      yield put(actionCreators.getMarkers.failure());
      break;
    }
    default: {
      // toast.error(messages.serverError, { type: 'default' });
      yield put(actionCreators.getMarkers.failure());
      break;
    }
  }
}

function* deleteMarker({ payload }) {
  const { id } = payload;
  const response = yield call(API.markers.delete, id);

  switch (response.code) {
    case 200: {
      const { items } = response;
      yield put(actionCreators.deleteMarker.success(id, items));
      break;
    }
    case 401: {
      toast.error(messages.sessionTimeout);
      yield put(logout());
      break;
    }
    case 404: {
      yield put(actionCreators.deleteMarker.failure(id, response.code));
      break;
    }
    default: {
      // toast.error(messages.serverError, { type: 'default' });
      yield put(actionCreators.deleteMarker.failure(id, response.code));
      break;
    }
  }
}

function* updateMarker({ payload }) {
  const { id, formData } = payload;

  const { markerForReplace } = yield select((state: AppState) => ({
    markerForReplace: state.map.markerForReplace,
  }));

  if (markerForReplace) {
    const formData = new URLSearchParams();

    formData.append('lat', markerForReplace.lat);
    formData.append('lng', markerForReplace.lng);
    formData.append('address', markerForReplace.address);
    formData.append('status', `${MarkerStatus.inactive}`);

    const response = yield call(API.markers.update, markerForReplace.id, formData);
    console.log({ response });
  }

  const response = yield call(API.markers.update, id, formData);

  switch (response.code) {
    case 200: {
      const { items } = response;
      yield put(actionCreators.updateMarker.success(id, items));
      break;
    }
    case 401: {
      toast.error(messages.sessionTimeout);
      yield put(logout());
      break;
    }
    case 404: {
      yield put(actionCreators.updateMarker.failure(id, response.code));
      break;
    }
    default: {
      // toast.error(messages.serverError, { type: 'default' });
      yield put(actionCreators.updateMarker.failure(id, response.code));
      break;
    }
  }
}

function* createMarker({ payload }) {
  const { lat, lng } = payload;

  const markerForReplace = yield select((state: AppState) => state.map.markerForReplace);

  if (markerForReplace) {
    console.log('create', { markerForReplace });
    const response = yield call(API.markers.delete, markerForReplace.id);
    console.log({ response });
  }

  const user = yield select((app: AppState) => app.auth.user);
  const locationData = yield call(getLocationData, { userId: user?.user_id || 0, lat, lng });

  if (locationData === null) {
    eventsService.showInfoModal({
      title: i18n.t('map.errorModal.title'),
      body: i18n.t('map.errorModal.osmError'),
      position: 'center',
    });

    yield put(
      sendLog({
        tag: `App:createMarker:Error`,
        error: 'locationData is NULL',
      })
    );

    yield put(actionCreators.createMarker.failure());
    return;
  }

  const response = yield call(API.markers.create, { data: locationData });
  const hasActiveMarker = yield select((app: AppState) => app.app.hasActiveMarker);

  switch (response.code) {
    case 200: {
      const { items } = response;
      yield put(actionCreators.createMarker.success(items));

      if (!hasActiveMarker) {
        yield put(getSearchStatus.request());
      }

      break;
    }
    case 401: {
      toast.error(messages.sessionTimeout);
      yield put(logout());
      break;
    }
    default: {
      // toast.error(messages.serverError, { type: 'default' });
      yield put(actionCreators.createMarker.failure());
      eventsService.showInfoModal({
        title: i18n.t('map.errorModal.title'),
        body: i18n.t('map.errorModal.serverError'),
        position: 'center',
      });
      yield put(
        sendLog({
          tag: `App:createMarker:Error`,
          skeddyResponse: response,
          locationData,
        })
      );
      break;
    }
  }
}

function* getClusters({ payload }) {
  const { lat, lng } = payload;
  const response = yield call(API.markers.clustersList, lat, lng);

  switch (response.code) {
    case 200: {
      const { clusters } = response;
      yield put(actionCreators.getClusters.success(clusters || []));
      break;
    }
    case 401: {
      toast.error(messages.sessionTimeout);
      yield put(logout());
      break;
    }
    default: {
      yield put(actionCreators.updateMarker.failure());
      break;
    }
  }
}

function* getRadius() {
  const response = yield call(API.settings.get);

  switch (response.code) {
    case 200: {
      const { radius } = response;
      yield put(actionCreators.getRadius.success(radius));
      break;
    }
    case 401: {
      toast.error(messages.sessionTimeout);
      yield put(logout());
      break;
    }
    default: {
      yield put(actionCreators.getRadius.failure());
      break;
    }
  }
}

function* getBestRides() {
  const response = yield call(loadBestRides);

  switch (response.code) {
    case 200: {
      yield put(actionCreators.getBestRides.success(response.rides));
      break;
    }
    case 401: {
      toast.error(messages.sessionTimeout);
      yield put(logout());
      break;
    }
    case 422: {
      yield put(actionCreators.getBestRides.failure());
      break;
    }
    default: {
      // toast.error(messages.serverError, { type: 'default' });
      yield put(actionCreators.getBestRides.failure());
      break;
    }
  }
}

function* getPlacesBySearch({ payload }) {
  const { search } = payload;

  const formData = new FormData();
  formData.append('data[service]', 'photon');
  formData.append('data[data][q]', search);

  const response = yield call(API.getInformation, formData);

  switch (response.status) {
    case 200: {
      const places = getPlaces(response);
      yield put(actionCreators.getPlacesBySearch.success(Object.values(places)));
      break;
    }
    case 401: {
      toast.error(messages.sessionTimeout);
      yield put(logout());
      break;
    }
    case 422: {
      yield put(actionCreators.getPlacesBySearch.failure());
      break;
    }
    default: {
      // toast.error(messages.serverError, { type: 'default' });
      yield put(actionCreators.getPlacesBySearch.failure());
      break;
    }
  }
}

export function* watchGetMarkers() {
  yield takeLatest(actionCreators.getMarkers.REQUEST, getMarkers);
}

export function* watchDeleteMarker() {
  yield takeEvery(actionCreators.deleteMarker.REQUEST, deleteMarker);
}

export function* watchUpdateMarker() {
  yield takeEvery(actionCreators.updateMarker.REQUEST, updateMarker);
}

export function* watchCreateMarker() {
  yield takeEvery(actionCreators.createMarker.REQUEST, createMarker);
}
export function* watchGetRadius() {
  yield takeEvery(actionCreators.getRadius.REQUEST, getRadius);
}

export function* watchGetClusters() {
  yield takeLatest(actionCreators.getClusters.REQUEST, getClusters);
}

export function* watchGetBestRides() {
  yield takeLatest(actionCreators.getBestRides.REQUEST, getBestRides);
}
export function* watchGetPlacesBySearch() {
  yield takeLatest(actionCreators.getPlacesBySearch.REQUEST, getPlacesBySearch);
}

export default function* flow() {
  yield fork(watchDeleteMarker);
  yield fork(watchGetClusters);
  yield fork(watchGetRadius);
  yield fork(watchCreateMarker);
  yield fork(watchGetBestRides);
  yield fork(watchUpdateMarker);
  yield fork(watchGetMarkers);
  yield fork(watchGetPlacesBySearch);
}
